import React, { useState, useEffect } from 'react';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import { GridFooterContainer } from '@mui/x-data-grid';
import { Box, Button, DialogContent, Select, MenuItem, Popover, Typography, FormControl, Pagination, PaginationItem } from '@mui/material';
// import Papa from 'papaparse';
import { useFormik } from 'formik';
import '../components/DeviceTable.css';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/system';
import CustomTextField from '../components/CustomTextField';
import CustomSelect from '../components/CustomSelect';
import koKR from '../components/koKR.json'; // Import the translation file
import AnalysisResultsDetailInfoDialog from './AnalysisResultsDetailInfoDialog'; // Import your custom dialog component
import CustomColumnSortedAscendingIcon from '../components/CustomColumnSortedAscendingIcon';
import CustomColumnSortedDescendingIcon from '../components/CustomColumnSortedDescendingIcon';
import apiClient from '../api/apiClient'; // API client import
import Config from '../Config'; // apiUrl 추가
import { getAccessToken } from '../utils/token';

const apiUrl = Config.apiUrl;

function CustomPagination(props) {
    // const apiRef = useGridApiContext(); // 사용되지 않음
    // const page = useGridSelector(apiRef, gridPageSelector); // 사용되지 않음
    const pageCount = Math.ceil(props.totalElements / props.size);
    const rowsPerPage = props.size;

    const handlePageChange = (event, value) => {
        props.onPageChange(value - 1); // DataGrid는 0-based page, Pagination은 1-based page
    };

    const handlePageSizeChange = (event) => {
        props.onPageSizeChange(parseInt(event.target.value, 10));
    };

    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 16px', width: '100%', marginBottom: '25px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body1">Showing</Typography>
                {/* <FormControl variant="outlined" sx={{ mx: 1, minWidth: 60, height: '2.5rem'  }}> */}
                <FormControl variant="outlined" sx={{ mx: 1, minWidth: 78, width: 78, height: 38 }}>
                    <Select value={rowsPerPage} onChange={handlePageSizeChange}
                        disableUnderline
                        sx={{ fontSize: '0.875rem', height: '100%', paddingTop: '8px', paddingBottom: '8px' }}>
                        {[10, 20, 30, 50].map((size) => (
                            <MenuItem key={size} value={size}>
                                {size}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Typography variant="body1">of {props.totalElements}</Typography>
            </Box>
            {props.totalElements > 0 && (
                <Pagination
                    count={pageCount}
                    page={props.page + 1}
                    onChange={handlePageChange}
                    // showFirstButton
                    // showLastButton
                    siblingCount={1}
                    boundaryCount={1}
                    shape="rounded"
                    color="primary"
                    variant='outlined'
                    renderItem={(item) => (
                        <PaginationItem
                            {...item}
                            sx={{
                                '&.Mui-selected': {
                                    backgroundColor: '#1976d2',
                                    color: '#fff',
                                },
                                backgroundColor: item.selected ? 'initial' : '#e0e0e0',
                                color: item.selected ? '#fff' : '#8b909a', // 선택되지 않았을 때 글자색 설정
                                border: 'none',
                                width: '28px',    // 가로 크기 설정
                                height: '28px',   // 세로 크기 설정
                                margin: '1px',    // 박스 간 간격 설정 (전체 간격이 2px이므로 양쪽에 1px씩)
                            }}
                        />
                    )}
                />
            )}
        </Box>
    );
}

const NoRowsOverlay = styled('div')({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    fontSize: '1.5rem',
    color: '#888',
    flexDirection: 'column',
});

function CustomFooter(props) {
    return (
        <GridFooterContainer sx={{ borderTop: 'none', color: '#8b909a' }}>
            <CustomPagination
                page={props.page}
                size={props.size}
                totalElements={props.totalElements}
                onPageChange={props.onPageChange}
                onPageSizeChange={props.onPageSizeChange}
            />
        </GridFooterContainer>
    );
}



const AnalysisResults = () => {
    const [rows, setRows] = useState([]);
    const [openFilterDialog, setOpenFilterDialog] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const apiRef = useGridApiRef();
    // const fileInputRef = useRef(null);
    const { i18n } = useTranslation();
    const { t } = useTranslation('console');
    const getRowHeight = (params) => 58;
    const [selectionModel, setSelectionModel] = React.useState([]);
    const [open, setOpen] = useState(false);
    const [selectedRowId, setSelectedRowId] = useState(null);
    // const [hoveredRow, setHoveredRow] = useState(null); // Track hovered row
    // State to track hover
    // const [hovered, setHovered] = useState(false);
    const [productsMenuItems, setProductsMenuItems] = useState([]); // Products menu items
    // const [originalRows, setOriginalRows] = useState([]); // 서버 필터링으로 인해 사용되지 않음
    // eslint-disable-next-line no-unused-vars
    const [filteredRows, setFilteredRows] = useState([]); // 필터링된 데이터를 저장할 상태
    const [loading, setLoading] = useState(true);

    // 새로운 필터링 파라미터 상태
    const [filterRequest, setFilterRequest] = useState({
        prodName: '',
        serial: '',
        lotNumber: '',
        errorCode: 0, // 기본값을 0으로 변경
    });

    // 새로운 페이지네이션 및 정렬 관련 상태
    const [page, setPage] = useState(0); // 0-based page index
    const [size, setSize] = useState(10); // Page size
    const [sort, setSort] = useState(['date,desc']); // Default sort
    const [totalElements, setTotalElements] = useState(0); // Total number of elements for pagination

    // API 데이터 호출
    const fetchAnalysisResults = async () => {
        setLoading(true);
        try {
            const token = getAccessToken();

            const requestParams = {};
            // filterRequest에서 값이 있는 필드만 추가
            if (filterRequest.prodName) requestParams.prodName = filterRequest.prodName;
            if (filterRequest.serial) requestParams.serial = filterRequest.serial;
            if (filterRequest.lotNumber) requestParams.lotNumber = filterRequest.lotNumber;
            // errorCode는 0이 기본값이므로, 0이 아닌 경우에만 추가
            if (filterRequest.errorCode !== 0) requestParams.errorCode = filterRequest.errorCode;

            const response = await apiClient.get(`${apiUrl}/console/analysis-results`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: {
                    ...requestParams, // 필터링 파라미터
                    page: page,
                    size: size,
                    sort: sort, // 정렬 파라미터 (배열 그대로 전달)
                },
            });

            const fetchedRows = response.data.content.map(item => ({
                id: item.id,
                order: item.order,
                model: item.prodName,
                serial: item.serial,
                analysis_time: item.date,
                cat_lot: item.lotNumber,
                error_code: item.errorCode,
            }));
            setRows(fetchedRows);
            setTotalElements(response.data.totalElements); // 총 데이터 수 설정
            // setOriginalRows(fetchedRows); // 초기 데이터 저장 (필터링을 위해) - 이제는 서버에서 필터링하므로 필요 없음
        } catch (error) {
            console.error('API 호출 실패:', error);
        } finally {
            setLoading(false);
        }
    };

    // API 데이터 호출 useEffect
    useEffect(() => {
        fetchAnalysisResults();
    }, [page, size, sort, filterRequest]); // filterRequest도 의존성 배열에 추가

    useEffect(() => {
        // 검사기 모델 데이터 불러오기
        const fetchProducts = async () => {
            try {
                const response = await apiClient.get(`${apiUrl}/console/devices/products`, {
                    headers: {
                        Authorization: `Bearer ${getAccessToken()}`,
                    },
                });

                const productsData = response.data.products.map((product) => ({
                    value: product.value,
                    label: product.text,
                }));

                setProductsMenuItems(productsData); // 받아온 countries 데이터를 설정
                console.log(productsData);
            } catch (error) {
                console.error('Error fetching countries:', error);
            }
        };

        fetchProducts();
    }, []);
    const handleRowClick = (params) => {
        setSelectedRowId(params.id); // 선택한 행의 id 설정
        setOpen(true);  // Open the DeviceManagementDialog
    };

    const handleClose = () => {
        setOpen(false);  // Close the DeviceManagementDialog
    };

    // const handleHeaderCheckboxChange = (event) => {
    //     if (event.target.checked) {
    //         const allIds = rows.map((row) => row.id); // 대체 코드
    //         setSelectionModel(allIds);
    //     } else {
    //         setSelectionModel([]);
    //     }
    // };

    const columns = [
        { field: 'order', headerName: `${t('analysis_result.column.id')}`, flex: 1, minWidth: 70, headerAlign: 'center', align: 'center' },
        { field: 'model', headerName: `${t('analysis_result.column.model')}`, flex: 2.5, minWidth: 100, headerAlign: 'center', align: 'center' },
        { field: 'serial', headerName: `${t('analysis_result.column.serial')}`, flex: 2, minWidth: 100, headerAlign: 'center', align: 'center' },
        { field: 'analysis_time', headerName: `${t('analysis_result.column.analysis_time')}`, flex: 2, minWidth: 100, headerAlign: 'center', align: 'center' },
        { field: 'cat_lot', headerName: `${t('analysis_result.column.cat_lot')}`, flex: 1, minWidth: 100, headerAlign: 'center', align: 'center' },
        { field: 'error_code', headerName: `${t('analysis_result.column.error_code')}`, flex: 1, minWidth: 100, headerAlign: 'center', align: 'center' },
    ];

    const formik = useFormik({
        initialValues: {
            model: '',
            serial: '',
            cat_lot: '',
            error_code: 0, // 기본값을 0으로 변경
        },
        onSubmit: (values) => {
            // 필터링 요청 파라미터 업데이트 및 페이지 0으로 초기화
            setFilterRequest({
                prodName: values.model,
                serial: values.serial,
                lotNumber: values.cat_lot,
                errorCode: values.error_code,
            });
            setPage(0); // 검색 시 첫 페이지로 이동
            handleCloseFilterDialog(); // 필터 다이얼로그 닫기
        },
    });

    const handleExport = async () => {
        try {
            const token = getAccessToken();

            const requestBody = {
                request: {},
                pageable: {
                    page: 0,
                    size: totalElements,
                    sort: sort,
                },
            };

            // filterRequest에서 값이 있는 필드만 requestBody.request에 추가
            if (filterRequest.prodName) requestBody.request.prodName = filterRequest.prodName;
            if (filterRequest.serial) requestBody.request.serial = filterRequest.serial;
            if (filterRequest.lotNumber) requestBody.request.lotNumber = filterRequest.lotNumber;
            if (filterRequest.errorCode !== 0) requestBody.request.errorCode = filterRequest.errorCode; // 0이 아닌 경우에만 추가

            const response = await apiClient.post(`${apiUrl}/console/analysis-results/export`, requestBody, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                responseType: 'blob', // 응답을 blob 형태로 받음
            });

            // 파일 다운로드 처리
            const blob = new Blob([response.data], { type: response.headers['content-type'] });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;

            // 파일 이름 가져오기 (Content-Disposition 헤더에서)
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'analysis_results.zip'; // 기본 파일 이름 변경
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename\*?="?([^;"]+)"?/);
                if (filenameMatch && filenameMatch[1]) {
                    try {
                        filename = decodeURIComponent(filenameMatch[1].replace(/\+/g, ' '));
                    } catch (e) {
                        filename = filenameMatch[1];
                    }
                }
            }
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);

        } catch (error) {
            console.error('Error exporting analysis results:', error);
        }
    };

    // 'View All' 버튼 클릭 시 전체 데이터 복원
    const handleViewAll = () => {
        setFilterRequest({
            prodName: '',
            serial: '',
            lotNumber: '',
            errorCode: 0,
        });
        setPage(0); // 첫 페이지로 이동
        formik.resetForm(); // 필터 검색 폼의 입력값과 선택값 초기화
    };

    // 'Filter Search' 버튼 클릭 시 필터 다이얼로그 토글
    const handleFilterSearch = (event) => {
        setAnchorEl(event.currentTarget);
        setOpenFilterDialog(!openFilterDialog);
    };

    // const handleClickFilterButton = (event) => {
    //     setAnchorEl(event.currentTarget);
    //     setOpenFilterDialog(!openFilterDialog);
    // };

    const handleCloseFilterDialog = () => {
        setOpenFilterDialog(false);
        setAnchorEl(null);
    };

    // function renderConnection(params) {
    //     console.log(params);
    //     return (
    //         <Box sx={{ textAlign: 'center', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    //             {params.value ? <img src="online.png" alt="online" /> : <img src="offline.png" alt="offline" />}
    //         </Box>
    //     );
    // }

    const getLocaleText = () => {
        return i18n.language === 'ko' ? koKR : {};
    };

    return (
        <Box sx={{ width: '1592px', minWidth: 1024 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h2" sx={{ fontSize: '32px', color: '#002a70', fontWeight: 'bold', mt: '20px' }}>
                    {t('analysis_result.header.title')}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', width: '1524px' }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: '-10px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Button
                            onClick={handleViewAll}
                            style={{
                                fontSize: '16px',
                                width: '178px',
                                backgroundColor: 'white',
                                border: '1px solid #ccc',
                                borderRadius: '12px 0 0 0',
                                height: '48px',
                                justifyContent: 'flex-start', // Align text to the left
                                paddingLeft: '16px', // Add some padding to the left
                                color: '#7d7d7d',
                                fontWeight: 'bold',
                            }}
                        >
                            {t('button.view_all')}
                        </Button>
                        <Button
                            onClick={handleFilterSearch}
                            style={{
                                fontSize: '16px',
                                width: '211px',
                                backgroundColor: 'white',
                                border: '1px solid #ccc',
                                borderRadius: '0 12px 0 0',
                                height: '48px',
                                justifyContent: 'flex-start', // Align text to the left
                                paddingLeft: '16px', // Add some padding to the left
                                paddingRight: '18px', // Add some padding to the right for endIcon
                                color: '#7d7d7d',
                                fontWeight: 'bold',
                            }}
                            startIcon={<img src={'/filter.png'} alt="Filter Icon" style={{ width: '30px', height: '30px' }} />}
                            endIcon={openFilterDialog ? <img src={'/up.png'} alt="Arrow Icon" style={{ width: '30px', height: '30px', marginLeft: '40px' }} /> :
                                <img src={'/down.png'} alt="Arrow Icon" style={{ width: '30px', height: '30px', marginLeft: '40px' }} />}
                        >
                            {t('button.filter_search')}
                        </Button>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '10px', width: 'calc(100% - 389px - 10px)' }}>
                    <Box className="button_container" sx={{ display: 'flex', gap: '10px' }}>
                        <Button
                            variant="contained"
                            onClick={handleExport}
                            className="button_dwload"
                            sx={{
                                fontSize: '14px',
                                borderRadius: '10px',
                                textTransform: 'none',
                                backgroundColor: '#80befc',
                                '&:hover': {
                                    backgroundColor: '#0b5cd5',
                                },
                            }}
                            endIcon={<img src={'/download.png'} alt="Download Icon" style={{ width: '30px', height: '30px' }} />}
                        >
                            {t('button.download')}
                        </Button>
                    </Box>
                </Box>
            </Box>
            <Box sx={{ width: '1524px', height: '756px', mt: 0 }}>
                {/* DataGrid 로딩 오버레이를 직접 사용하도록 수정 */}
                <DataGrid
                    rows={rows}
                    columns={columns}
                    apiRef={apiRef}
                    getRowId={(row) => row.id}
                    getRowHeight={getRowHeight}
                    headerHeight={48}
                    localeText={getLocaleText()} // Use the localeText based on the current locale
                    onRowClick={handleRowClick} // 행 클릭 시 이벤트
                    loading={loading} // DataGrid의 loading prop을 활성화
                    rowCount={totalElements}
                    pageSizeOptions={[10, 20, 30, 50]}
                    paginationModel={{ page, pageSize: size }}
                    onPaginationModelChange={(newModel) => {
                        setPage(newModel.page);
                        setSize(newModel.pageSize);
                    }}
                    sortingMode="server"
                    sortModel={sort.map(s => ({ field: s.split(',')[0], sort: s.split(',')[1] }))}
                    onSortModelChange={(newSortModel) => {
                        setSort(newSortModel.map(s => `${s.field},${s.sort}`));
                    }}
                    paginationMode="server"
                    slots={{
                        footer: () => (
                            <CustomFooter
                                page={page}
                                size={size}
                                totalElements={totalElements}
                                onPageChange={setPage}
                                onPageSizeChange={setSize}
                            />
                        ),
                        noRowsOverlay: () => (loading ? null : ( // 로딩 중일 때는 noRowsOverlay를 숨김
                            <NoRowsOverlay>
                                <img src="nodata.png" alt="No data" />
                            </NoRowsOverlay>
                        )),
                        columnSortedAscendingIcon: CustomColumnSortedAscendingIcon,
                        columnSortedDescendingIcon: CustomColumnSortedDescendingIcon,
                    }}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 10 } },
                    }}
                    disableSelectionOnClick
                    selectionModel={selectionModel}
                    onSelectionModelChange={(newSelection) => {
                        setSelectionModel(newSelection);
                    }}
                    sx={{
                        '& .MuiDataGrid-columnHeaders div[role="row"]': {
                            backgroundColor: '#F5F5F7',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            textAlign: 'center',
                            display: 'flex',
                            justifyContent: 'center',
                            width: '100%',
                            fontSize: '14px',
                            fontWeight: '800',
                            color: '#7d7d7d',
                        },
                        '& .MuiDataGrid-cell': {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            fontSize: '16px',
                            color: '#494949',
                            height: '58px',
                            backgroundColor: '#ffffff',
                        },
                        '& .MuiDataGrid-cellContent': {
                            width: '100%',
                        },
                        '& .MuiDataGrid-footerContainer': {
                            display: 'flex',
                            justifyContent: 'center',
                            marginTop: '50px',
                            backgroundColor: '#ffffff',
                        },
                        borderBottomLeftRadius: '14px',  // 왼쪽 아래 모서리 라운드 적용
                        borderBottomRightRadius: '14px', // 오른쪽 아래 모서리 라운드 적용
                        overflow: 'hidden',
                        backgroundColor: '#ffffff',
                    }}
                />
            </Box>
            {/* DeviceManagementDialog Component */}
            <AnalysisResultsDetailInfoDialog
                open={open}
                onClose={handleClose}
                selectedRow={selectedRowId} // Pass the selected row to the dialog for context
            />
            <Popover
                open={openFilterDialog}
                anchorEl={anchorEl}
                onClose={handleCloseFilterDialog}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    style: {
                        width: 448,
                        height: 431,
                        borderRadius: '8px',
                        border: '1px solid #80befc',
                    },
                }}
            >
                <DialogContent>
                    <form onSubmit={formik.handleSubmit}>
                        <Box sx={{ mt: '10px', ml: '21px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', gap: '24px' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Typography sx={{ color: '#002a70' }}>{t('analysis_result.filter_search.device_model')}</Typography>
                                    <CustomSelect
                                        id="model"
                                        name="model"
                                        value={formik.values.model}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        menuItems={productsMenuItems}
                                        placeholder={t('analysis_result.filter_search.device_model_placeholder')}
                                        // description="Select a language"
                                        width="322px"   // Custom width
                                        height="48px"   // Custom height
                                        // fontSize="18px" // Custom font size
                                        itemWidth="151px"  // Custom Menu Item width
                                        itemHeight="42px"  // Custom Menu Item height
                                    />
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: '24px' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Typography sx={{ color: '#002a70' }}>{t('analysis_result.filter_search.serial')}</Typography>
                                    <CustomTextField
                                        id="serial"
                                        name="serial"
                                        placeholder={t('analysis_result.filter_search.serial_placeholder')}
                                        // description="This will be device serial number"
                                        error={false}
                                        disabled={false}
                                        value={formik.values.serial}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        // error={formik.touched.myTextField && Boolean(formik.errors.myTextField)}
                                        // helperText={formik.touched.myTextField && formik.errors.myTextField}
                                        active={true}
                                        size="medium"
                                        width="268px"   // 가로 크기 지정
                                        height="48px"   // 세로 크기 지정
                                    />
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', gap: '24px' }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Typography sx={{ color: '#002a70' }}>{t('analysis_result.filter_search.cat_lot')}</Typography>
                                    <CustomTextField
                                        id="cat_lot"
                                        name="cat_lot"
                                        placeholder={t('analysis_result.filter_search.cat_lot_placeholder')}
                                        // description="This will be device serial number"
                                        error={false}
                                        disabled={false}
                                        value={formik.values.cat_lot}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        // error={formik.touched.myTextField && Boolean(formik.errors.myTextField)}
                                        // helperText={formik.touched.myTextField && formik.errors.myTextField}
                                        active={true}
                                        size="medium"
                                        width="171px"   // 가로 크기 지정
                                        height="48px"   // 세로 크기 지정
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Typography sx={{ color: '#002a70' }}>{t('analysis_result.filter_search.error_code')}</Typography>
                                    <CustomTextField
                                        id="error_code"
                                        name="error_code"
                                        placeholder={t('analysis_result.filter_search.error_code_placeholder')}
                                        // description="This will be device serial number"
                                        error={false}
                                        disabled={false}
                                        value={formik.values.error_code}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        // error={formik.touched.myTextField && Boolean(formik.errors.myTextField)}
                                        // helperText={formik.touched.myTextField && formik.errors.myTextField}
                                        active={true}
                                        size="medium"
                                        width="171px"   // 가로 크기 지정
                                        height="48px"   // 세로 크기 지정
                                    />
                                </Box>
                            </Box>
                            <Box sx={{ mt: '46px', display: 'flex', justifyContent: 'center', gap: '28px' }}>
                                <Button
                                    onClick={handleCloseFilterDialog}
                                    variant="outlined"
                                    sx={{ color: '#8b8fa8', border: 'solid 1px var(--gray-gray-200)', fontSize: '16px', width: '160px', height: '48px', borderRadius: '10px' }}
                                >
                                    {t('button.cancel')}
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    sx={{ backgroundColor: "#007dfa", fontSize: '16px', width: '160px', height: '48px', borderRadius: '10px' }}
                                >
                                    {t('button.search')}
                                </Button>
                            </Box>
                        </Box>
                    </form>
                </DialogContent>
            </Popover>
        </Box>
    );
};

export default AnalysisResults;